import type { PlainMessage } from "@bufbuild/protobuf";
import type { Timestamp } from "@bufbuild/protobuf";
import type { DailyCreditUsage, UserCreditUsage, TeamCreditUsage } from "gitpod-next-api/gitpod/v1/billing_pb";
import { UsageType } from "gitpod-next-api/gitpod/v1/billing_pb";

// ─── Constants ──────────────────────────────────────────────────────────────

/** Maximum named user segments shown per day. */
export const DEFAULT_TOP_N = 5;

/** The backend uses a zero-UUID sentinel for unteamed/unattributed usage. */
const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

/** Returns true when the teamId represents a real team (not unteamed). */
function isRealTeamId(teamId: string): boolean {
    return teamId !== "" && teamId !== ZERO_UUID;
}

/** Key used for the i-th rank slot in chart data points (e.g. "rank_0"). */
export function rankSlotKey(i: number): string {
    return `rank_${i}`;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type Grouping = "type" | "users" | "teams";
export type UsageTypeFilter = "all" | "environment" | "agentic";

export type MonthRange = {
    /** Unique key, e.g. "2026-02" */
    value: string;
    /** Display label, e.g. "Jan 2026" */
    label: string;
    /** Inclusive start of the range */
    start: Date;
    /** Inclusive end of the range */
    end: Date;
};

export type TopUser = {
    userId: string;
    displayName: string;
    total: number;
    environment: number;
    agentic: number;
};

export type SlotUser = { userId: string; displayName: string };
export type SlotTeam = { teamId: string; displayName: string };

export type DailyChartPoint = {
    date: string;
    total: number;
    environment: number;
    agentic: number;
    /** Maps rank slot key (rank_0 … rank_N) → user shown in that slot for this day. */
    slotUsers?: Record<string, SlotUser>;
    /** Maps rank slot key (rank_0 … rank_N) → team shown in that slot for this day. */
    slotTeams?: Record<string, SlotTeam>;
    [key: string]: string | number | Record<string, SlotUser> | Record<string, SlotTeam> | undefined;
};

type PlainDailyCreditUsage = PlainMessage<DailyCreditUsage>;
type PlainUserCreditUsage = PlainMessage<UserCreditUsage>;

// ─── Formatting ─────────────────────────────────────────────────────────────

/** Ceiling-round to the nearest whole number. */
export function ceilCredits(n: number): number {
    return Math.ceil(n);
}

export function formatCredits(n: number): string {
    const v = ceilCredits(n);
    if (v >= 1_000_000) return `${Math.ceil(v / 1_000_000)}M`;
    if (v >= 1_000) return `${Math.ceil(v / 1_000)}k`;
    return v.toLocaleString();
}

/** Format credits as an exact number with locale grouping (no k/M suffix). */
export function formatCreditsExact(n: number): string {
    return ceilCredits(n).toLocaleString();
}

export function formatDateShort(ts: PlainMessage<Timestamp> | undefined): string {
    if (!ts) return "";
    const d = new Date(Number(ts.seconds) * 1000);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

export function formatUpdatedAt(ts: PlainMessage<Timestamp> | undefined): string {
    if (!ts) return "";
    const d = new Date(Number(ts.seconds) * 1000);
    const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: "UTC" });
    return `${date} at ${time} UTC`;
}

// ─── Month Ranges ───────────────────────────────────────────────────────────

/**
 * Current partial month + 5 previous full months. Dates are in UTC to avoid timezone-induced range overflow.
 * When periodStart is provided, months that end before the period start are excluded.
 */
export function buildMonthRanges(referenceDate: Date = new Date(), periodStart?: Date): MonthRange[] {
    const ranges: MonthRange[] = [];

    for (let i = 0; i < 6; i++) {
        // Use UTC to avoid local-timezone offsets that push start/end into adjacent days.
        const refYear = referenceDate.getUTCFullYear();
        const refMonth = referenceDate.getUTCMonth();
        const year = refMonth - i < 0 ? refYear - 1 : refYear;
        const month = (((refMonth - i) % 12) + 12) % 12;

        const start = new Date(Date.UTC(year, month, 1));
        // For the current month, end is now; for past months, end is last instant of the month.
        const end = i === 0 ? referenceDate : new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));

        // Skip months that end before the period start date.
        if (periodStart && end < periodStart) {
            continue;
        }

        const key = `${year}-${String(month + 1).padStart(2, "0")}`;
        // Format label from the UTC date to avoid month mismatch near midnight.
        const label = `${start.toLocaleDateString(undefined, { month: "short", timeZone: "UTC" })} ${year}`;

        ranges.push({ value: key, label, start, end });
    }

    return ranges;
}

// ─── Data Extraction ────────────────────────────────────────────────────────

function creditsForType(usage: PlainDailyCreditUsage, type: UsageType): number {
    return usage.orgUsage.find((u) => u.usageType === type)?.credits ?? 0;
}

function userCreditsForType(user: PlainUserCreditUsage, type: UsageType): number {
    return user.usage.find((u) => u.usageType === type)?.credits ?? 0;
}

function totalOrgCredits(usage: PlainDailyCreditUsage): number {
    return usage.orgUsage.reduce((sum, u) => sum + u.credits, 0);
}

/** Collect unique users from user_usage across all days. Excludes "Others" bucket (empty userId). */
export function extractUniqueUsers(dailyUsage: PlainDailyCreditUsage[]): { userId: string; displayName: string }[] {
    const seen = new Map<string, string>();
    for (const day of dailyUsage) {
        for (const user of day.userUsage) {
            if (user.userId && !seen.has(user.userId)) {
                seen.set(user.userId, user.displayName);
            }
        }
    }
    return Array.from(seen.entries())
        .map(([userId, displayName]) => ({ userId, displayName }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

// ─── Aggregation ────────────────────────────────────────────────────────────

/**
 * Collect the union of per-day top-N users for use as named chart segments.
 *
 * For each day we rank the named users (excluding the backend's "Others"
 * bucket) by that day's credits and pick the top N. The returned list is the
 * union of all per-day winners, sorted by aggregate total for legend ordering.
 * The list may contain more than topN entries when different days have
 * different top users.
 */
export function computeTopUsers(dailyUsage: PlainDailyCreditUsage[], topN: number): TopUser[] {
    const topUserIds = new Set<string>();

    // Determine each day's own top-N users.
    for (const day of dailyUsage) {
        const named = day.userUsage
            .filter((u) => u.userId)
            .map((u) => ({
                userId: u.userId,
                total: u.usage.reduce((s, c) => s + c.credits, 0),
            }))
            .sort((a, b) => b.total - a.total)
            .slice(0, topN);

        for (const u of named) {
            topUserIds.add(u.userId);
        }
    }

    // Aggregate totals for the union so the legend is sorted by overall usage.
    const byUser = new Map<string, TopUser>();
    for (const day of dailyUsage) {
        for (const user of day.userUsage) {
            if (!topUserIds.has(user.userId)) continue;

            const env = userCreditsForType(user, UsageType.ENVIRONMENT);
            const agentic = userCreditsForType(user, UsageType.AGENTIC);
            const total = env + agentic;

            const existing = byUser.get(user.userId);
            if (existing) {
                existing.total += total;
                existing.environment += env;
                existing.agentic += agentic;
            } else {
                byUser.set(user.userId, {
                    userId: user.userId,
                    displayName: user.displayName,
                    total,
                    environment: env,
                    agentic,
                });
            }
        }
    }

    return Array.from(byUser.values()).sort((a, b) => b.total - a.total);
}

/** Sum credits across all days, respecting filters. */
export function computeTotalCredits(
    dailyUsage: PlainDailyCreditUsage[],
    usageTypeFilter: UsageTypeFilter = "all",
    userFilter?: string,
): number {
    let total = 0;

    for (const day of dailyUsage) {
        if (userFilter) {
            // Sum from user_usage for the specific user
            for (const user of day.userUsage) {
                if (user.userId !== userFilter) continue;
                if (usageTypeFilter === "all") {
                    total += user.usage.reduce((s, u) => s + u.credits, 0);
                } else {
                    const type = usageTypeFilter === "environment" ? UsageType.ENVIRONMENT : UsageType.AGENTIC;
                    total += userCreditsForType(user, type);
                }
            }
        } else {
            // Sum from org_usage
            if (usageTypeFilter === "all") {
                total += totalOrgCredits(day);
            } else {
                const type = usageTypeFilter === "environment" ? UsageType.ENVIRONMENT : UsageType.AGENTIC;
                total += creditsForType(day, type);
            }
        }
    }

    return total;
}

// ─── Chart Data ─────────────────────────────────────────────────────────────

/** UTC "YYYY-MM-DD" key for indexing daily data. */
function utcDateKey(d: Date): string {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/** Midnight UTC for the given date, stripping time components. */
function utcDayStart(d: Date): Date {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** Format a plain JS Date the same way formatDateShort formats a Timestamp. */
function formatDayUTC(d: Date): string {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Build a chart point for a day that has no API data. */
function emptyPoint(dateLabel: string): DailyChartPoint {
    return { date: dateLabel, total: 0, environment: 0, agentic: 0 };
}

export type BuildDailyChartDataOptions = {
    usageTypeFilter?: UsageTypeFilter;
    userFilter?: string;
    perDayTopN?: number;
    monthStart?: Date;
    monthEnd?: Date;
};

/**
 * Transform API response into recharts-compatible data points.
 *
 * When `monthStart`/`monthEnd` are provided and the API returned at least one
 * day of data, the output covers every day in that range — filling zeros for
 * days without API data. This keeps the chart full-width even early in the
 * month. When the API returns no data at all, an empty array is returned so
 * the chart's empty state renders.
 */
export function buildDailyChartData(
    dailyUsage: PlainDailyCreditUsage[],
    grouping: Grouping,
    _topUsers: TopUser[],
    options: BuildDailyChartDataOptions = {},
): DailyChartPoint[] {
    const { usageTypeFilter = "all", userFilter, perDayTopN = DEFAULT_TOP_N, monthStart, monthEnd } = options;
    // Index API data by UTC date key for O(1) lookup.
    const byDateKey = new Map<string, PlainDailyCreditUsage>();
    for (const day of dailyUsage) {
        if (!day.date) continue;
        const d = new Date(Number(day.date.seconds) * 1000);
        byDateKey.set(utcDateKey(d), day);
    }

    // Build the list of dates to render.
    // Only pad to the full month when there is actual data — an empty API
    // response should produce an empty array so the chart's empty state shows.
    const dates: Date[] = [];
    if (monthStart && monthEnd && byDateKey.size > 0) {
        const cursor = utcDayStart(monthStart);
        const endDay = utcDayStart(monthEnd);
        while (cursor <= endDay) {
            dates.push(new Date(cursor));
            cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
    } else {
        // Fallback: use only the dates present in the API response.
        for (const day of dailyUsage) {
            if (!day.date) continue;
            dates.push(new Date(Number(day.date.seconds) * 1000));
        }
    }

    return dates.map((d) => {
        const day = byDateKey.get(utcDateKey(d));
        const dateLabel = formatDayUTC(d);

        if (!day) {
            return emptyPoint(dateLabel);
        }

        const point: DailyChartPoint = { date: dateLabel, total: 0, environment: 0, agentic: 0 };

        if (userFilter && grouping === "type") {
            // By-type with member highlight: split each type into member + rest within the same stack.
            // Stack order (bottom→top): member_environment, rest_environment, member_agentic, rest_agentic
            const env = usageTypeFilter !== "agentic" ? creditsForType(day, UsageType.ENVIRONMENT) : 0;
            const agentic = usageTypeFilter !== "environment" ? creditsForType(day, UsageType.AGENTIC) : 0;
            let memberEnv = 0;
            let memberAgentic = 0;
            for (const user of day.userUsage) {
                if (user.userId !== userFilter) continue;
                memberEnv = usageTypeFilter !== "agentic" ? userCreditsForType(user, UsageType.ENVIRONMENT) : 0;
                memberAgentic = usageTypeFilter !== "environment" ? userCreditsForType(user, UsageType.AGENTIC) : 0;
            }
            point.member_environment = memberEnv;
            point.rest_environment = Math.max(0, env - memberEnv);
            point.member_agentic = memberAgentic;
            point.rest_agentic = Math.max(0, agentic - memberAgentic);
            point.environment = env;
            point.agentic = agentic;
            point.total = env + agentic;
        } else if (userFilter && grouping === "users") {
            // Top Users with member selected: member vs others
            for (const user of day.userUsage) {
                const credits = userCreditsForFilter(user, usageTypeFilter);
                if (user.userId === userFilter) {
                    point[userFilter] = credits;
                } else {
                    point.others = ((point.others as number) || 0) + credits;
                }
                point.total += credits;
            }
        } else if (grouping === "users") {
            // Rank this day's named users and assign the top N to positional
            // rank slots (rank_0 … rank_N-1). Each slot stacks in a fixed
            // position so the gradient is always darkest-at-top. Users outside
            // the daily top N and the backend's "Others" bucket go to others.
            const ranked = day.userUsage
                .filter((u) => u.userId)
                .map((u) => ({ user: u, credits: userCreditsForFilter(u, usageTypeFilter) }))
                .sort((a, b) => b.credits - a.credits);

            const slotUsers: Record<string, SlotUser> = {};

            for (let i = 0; i < ranked.length; i++) {
                const { user, credits } = ranked[i];
                if (i < perDayTopN) {
                    const key = rankSlotKey(i);
                    point[key] = credits;
                    slotUsers[key] = { userId: user.userId, displayName: user.displayName };
                } else {
                    point.others = ((point.others as number) || 0) + credits;
                }
                point.total += credits;
            }

            // Backend's "Others" bucket (empty userId)
            for (const user of day.userUsage) {
                if (user.userId) continue;
                const credits = userCreditsForFilter(user, usageTypeFilter);
                point.others = ((point.others as number) || 0) + credits;
                point.total += credits;
            }

            point.slotUsers = slotUsers;
        } else {
            // All or by-type: use org_usage
            const env = usageTypeFilter !== "agentic" ? creditsForType(day, UsageType.ENVIRONMENT) : 0;
            const agentic = usageTypeFilter !== "environment" ? creditsForType(day, UsageType.AGENTIC) : 0;
            point.environment = env;
            point.agentic = agentic;
            point.total = env + agentic;
        }

        return point;
    });
}

/** Sum credits from a usage-by-type array, respecting the active filter. */
function creditsFromUsageArray(
    usage: readonly { usageType: UsageType; credits: number }[],
    usageTypeFilter: UsageTypeFilter,
): number {
    if (usageTypeFilter === "all") {
        return usage.reduce((s, u) => s + u.credits, 0);
    }
    const type = usageTypeFilter === "environment" ? UsageType.ENVIRONMENT : UsageType.AGENTIC;
    return usage.find((u) => u.usageType === type)?.credits ?? 0;
}

/** Credits for a single user entry, respecting the usage-type filter. */
function userCreditsForFilter(user: PlainUserCreditUsage, usageTypeFilter: UsageTypeFilter): number {
    return creditsFromUsageArray(user.usage, usageTypeFilter);
}

// ─── Team Chart Data ────────────────────────────────────────────────────────

type PlainTeamCreditUsage = PlainMessage<TeamCreditUsage>;

/** Credits for a single team entry, respecting the usage-type filter. */
function teamCreditsForFilter(team: PlainTeamCreditUsage, usageTypeFilter: UsageTypeFilter): number {
    return creditsFromUsageArray(team.usage, usageTypeFilter);
}

/**
 * Build chart data for the "teams" grouping using the teamUsage field
 * from DailyCreditUsage (populated when the preaggregation flag is on).
 */
export function buildTeamDailyChartData(
    dailyUsage: PlainDailyCreditUsage[],
    usageTypeFilter: UsageTypeFilter,
    monthStart: Date,
    monthEnd: Date,
    perDayTopN = DEFAULT_TOP_N,
): DailyChartPoint[] {
    // Index API data by UTC date key for O(1) lookup.
    const byDateKey = new Map<string, PlainDailyCreditUsage>();
    for (const day of dailyUsage) {
        if (!day.date) continue;
        const d = new Date(Number(day.date.seconds) * 1000);
        byDateKey.set(utcDateKey(d), day);
    }

    const dates: Date[] = [];
    if (byDateKey.size > 0) {
        const cursor = utcDayStart(monthStart);
        const endDay = utcDayStart(monthEnd);
        while (cursor <= endDay) {
            dates.push(new Date(cursor));
            cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
    }

    return dates.map((d) => {
        const dateLabel = formatDayUTC(d);
        const day = byDateKey.get(utcDateKey(d));

        if (!day || day.teamUsage.length === 0) {
            return emptyPoint(dateLabel);
        }

        const point: DailyChartPoint = { date: dateLabel, total: 0, environment: 0, agentic: 0 };

        // Rank teams by credits for this day.
        // Exclude unteamed entries (empty teamId or zero-UUID sentinel) from ranking.
        const ranked = day.teamUsage
            .filter((t) => isRealTeamId(t.teamId))
            .map((t) => ({ team: t, credits: teamCreditsForFilter(t, usageTypeFilter) }))
            .sort((a, b) => b.credits - a.credits);

        const slotTeams: Record<string, SlotTeam> = {};

        for (let i = 0; i < ranked.length; i++) {
            const { team, credits } = ranked[i];
            if (i < perDayTopN) {
                const key = rankSlotKey(i);
                point[key] = credits;
                slotTeams[key] = { teamId: team.teamId, displayName: team.displayName };
            } else {
                point.others = ((point.others as number) || 0) + credits;
            }
            point.total += credits;
        }

        // Unteamed usage (empty teamId or zero-UUID sentinel) goes to "others"
        for (const team of day.teamUsage) {
            if (isRealTeamId(team.teamId)) continue;
            const credits = teamCreditsForFilter(team, usageTypeFilter);
            point.others = ((point.others as number) || 0) + credits;
            point.total += credits;
        }

        point.slotTeams = slotTeams;
        return point;
    });
}
